/**
 * Sales unit — does a listing price cover ONE item, or ONE full set?
 *
 * The problem this solves: today a listing shows "6 499 kr" directly above
 * "Antal: 4 st". A buyer reads that as "four wheels for 6 499 kr", but it is
 * usually the price of one wheel (~26 000 kr for the set). The same "Antal"
 * row also means stock on other listings, so it means two different things.
 *
 * The model (BR-1): a listing price is ALWAYS the price of one *sales unit*.
 * Two fields in `publicData` say what that unit is:
 *
 *   salesUnit  'piece' | 'set'   what one price covers
 *   setSize    integer >= 1      how many items are in one set
 *
 * Everything else is derived here and never stored, so the stored price and
 * the shown price can never drift apart:
 *
 *   set total   = price * setSize   (when salesUnit = 'piece')
 *   per item    = price / setSize   (when salesUnit = 'set')
 *
 * A third field, `salesUnitConfirmed`, marks whether the seller has actually
 * confirmed the unit or whether the backfill guessed it (BR-11). Listings are
 * still shown with their inferred unit — that is clearer than today — but the
 * seller gets a one-time banner asking them to confirm.
 *
 * STOCK is deliberately NOT a field here. Sharetribe already tracks stock on
 * the listing (`currentStock`), decrements it on purchase and restores it on a
 * cancelled order. Adding a second `publicData.stockQuantity` would create two
 * numbers that drift apart the first time a purchase happens. See
 * `describeStock` below, which reads the Sharetribe value.
 *
 * TWIN FILE: server/api-util/salesUnit.js is the CommonJS copy used by the CSV
 * import, the package publisher, the merchant feed and the backfill script.
 * Keep both in sync.
 */

import { isStaggeredListing } from './listingCombination';

export const SALES_UNIT_PIECE = 'piece';
export const SALES_UNIT_SET = 'set';

/** Four is the overwhelmingly common set on this marketplace. */
export const DEFAULT_SET_SIZE = 4;
export const MIN_SET_SIZE = 1;
/** A dually / spare-carrying set can reach 6; 12 is a generous ceiling. */
export const MAX_SET_SIZE = 12;

/** At or below this many items left, a company listing reads "Få kvar". */
export const LOW_STOCK_THRESHOLD = 2;

/* The listing-type trap this codebase already carries (see listingCombination.js):
 * `businesswheel` (no dash) is a RIM listing, `business-wheel` (dash) is a
 * complete wheel. `adType` uses the short private-side vocabulary. */
const WHEEL_TYPES = ['wheel', 'business-wheel'];
const TIRE_TYPES = ['tire', 'businesstire'];
const RIM_TYPES = ['rim', 'businesswheel'];

const NOUNS = {
  wheel: { one: 'hjul', many: 'hjul' },
  tire: { one: 'däck', many: 'däck' },
  rim: { one: 'fälg', many: 'fälgar' },
};

/**
 * BR-6 / BR-13 — the "this set price looks too low" figure, per category, in
 * kronor per single item.
 *
 * The warning fires when a SET price works out to less per item than the
 * amount below, i.e. when a set of four is priced roughly like one item.
 *
 * IMPORTANT: these are starting figures, not agreed business numbers — BR-13
 * leaves the real per-category figure open.
 *
 * MUST stay identical to the table in server/api-util/salesUnit.js. The wizard
 * warns the seller using this table; the CSV import rejects a row using that
 * one. Two different numbers would let a seller pass the wizard and then have
 * the very same price rejected on import. To change one, edit BOTH files.
 */
export const MIN_PLAUSIBLE_PIECE_PRICE_SEK = {
  wheel: 2000, // complete wheel (rim + tyre)
  rim: 1000,
  tire: 500,
};

const clean = value => (value === null || value === undefined ? '' : String(value).trim());

/** 'wheel' | 'tire' | 'rim' — the category this listing belongs to. */
export const getProductKind = (publicData = {}) => {
  const type = clean(publicData.adType) || clean(publicData.listingType);
  if (WHEEL_TYPES.includes(type)) return 'wheel';
  if (TIRE_TYPES.includes(type)) return 'tire';
  if (RIM_TYPES.includes(type)) return 'rim';
  return 'wheel';
};

/** The word a buyer sees: "hjul" / "däck" / "fälg" (BR-2). */
export const getUnitNoun = (publicData = {}) => NOUNS[getProductKind(publicData)];

/* The listing types that are actually SOLD. Everything else on this
 * marketplace is a different kind of record that happens to be stored as a
 * listing: `buying` is a buy request (a wanted ad, no price, no stock),
 * `admin-adverts` is a promo slot, and storage items are internal drafts. */
const SELLABLE_TYPES = [...WHEEL_TYPES, ...TIRE_TYPES, ...RIM_TYPES];

/**
 * Does a sales unit mean anything for this listing?
 *
 * A buy request has nothing for sale, so "Säljs som: Komplett sats · 4 hjul"
 * on one is nonsense — it describes goods that do not exist. Anything that is
 * not a sell listing must be left alone entirely, in the UI and in the data.
 *
 * Checks `listingType` first and only falls back to `adType`: a buy request
 * legitimately carries an adType for the kind of wheel someone WANTS, so
 * trusting adType here would classify it as sellable.
 */
export const hasSalesUnit = (publicData = {}) => {
  const listingType = clean(publicData.listingType);
  if (listingType) return SELLABLE_TYPES.includes(listingType);
  return SELLABLE_TYPES.includes(clean(publicData.adType));
};

/** True for the company-side listing types (business-wheel / businesstire / businesswheel). */
export const isCompanyListingType = listingType => clean(listingType).startsWith('business');

/**
 * BR-5 default: private sellers price the whole set (their one set IS the
 * listing), companies price per item (they hold stock of individual items).
 */
export const defaultSalesUnit = ({ isCompany }) =>
  isCompany ? SALES_UNIT_PIECE : SALES_UNIT_SET;

const toSetSize = value => {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n)) return DEFAULT_SET_SIZE;
  return Math.min(MAX_SET_SIZE, Math.max(MIN_SET_SIZE, n));
};

/**
 * Read the sales unit off a listing's publicData, filling in defaults and
 * applying the rules that are not the seller's to choose.
 *
 * @returns {{salesUnit: string, setSize: number, confirmed: boolean, lockedToSet: boolean}}
 *   `lockedToSet` is true when the listing can never be sold per item — a
 *   staggered listing (BR-10) cannot express "2 front + 2 rear" with a
 *   quantity picker, and a Bygg paket package is a set by definition (BR-8).
 */
export const resolveSalesUnit = (publicData = {}) => {
  const setSize = toSetSize(publicData.setSize);
  // `isReachShell` is the flag the Bygg paket publisher writes on a package.
  const lockedToSet = isStaggeredListing(publicData) || publicData.isReachShell === true;

  const stored = clean(publicData.salesUnit);
  const isCompany = isCompanyListingType(publicData.listingType);
  const salesUnit =
    stored === SALES_UNIT_PIECE || stored === SALES_UNIT_SET
      ? stored
      : defaultSalesUnit({ isCompany });

  return {
    salesUnit: lockedToSet ? SALES_UNIT_SET : salesUnit,
    setSize,
    confirmed: publicData.salesUnitConfirmed === true,
    lockedToSet,
  };
};

/** Total for one full set, in currency subunits. */
export const setAmount = (unitAmount, { salesUnit, setSize }) =>
  salesUnit === SALES_UNIT_SET ? unitAmount : unitAmount * setSize;

/**
 * Price of one item, in currency subunits. Rounded, because a set price does
 * not always divide evenly — the set total stays the authoritative number.
 */
export const pieceAmount = (unitAmount, { salesUnit, setSize }) =>
  salesUnit === SALES_UNIT_SET ? Math.round(unitAmount / setSize) : unitAmount;

/**
 * BR-2 — the plain-words line that sits directly under the price.
 *
 *   per item:  "Pris per hjul · 25 996 kr för 4"
 *   full set:  "Pris för en komplett sats (4 hjul) · 6 499 kr per hjul"
 *
 * @param {Object}   params
 * @param {number}   params.unitAmount   the listing price, in currency subunits
 * @param {Object}   params.publicData   the listing's publicData
 * @param {Function} params.formatAmount subunits -> display string, e.g.
 *   `amount => customFormatMoney(intl, new Money(amount, currency))`
 * @returns {string|null} null when there is no usable price
 */
export const priceUnitLine = ({ unitAmount, publicData, formatAmount }) => {
  if (!Number.isFinite(unitAmount) || unitAmount <= 0) return null;

  const { salesUnit, setSize } = resolveSalesUnit(publicData);
  const noun = getUnitNoun(publicData);

  // A "set" of one is just one item — saying so twice only confuses.
  if (setSize <= 1) return `Pris per ${noun.one}`;

  return salesUnit === SALES_UNIT_SET
    ? `Pris för en komplett sats (${setSize} ${noun.many}) · ` +
        `${formatAmount(pieceAmount(unitAmount, { salesUnit, setSize }))} per ${noun.one}`
    : `Pris per ${noun.one} · ` +
        `${formatAmount(setAmount(unitAmount, { salesUnit, setSize }))} för ${setSize}`;
};

/**
 * BR-3 — the row that replaces the old bare "Antal" quantity.
 * @returns {{label: string, value: string}}
 */
export const soldAsRow = publicData => {
  const { salesUnit, setSize } = resolveSalesUnit(publicData);
  const noun = getUnitNoun(publicData);
  return {
    label: 'Säljs som',
    value:
      salesUnit === SALES_UNIT_SET && setSize > 1
        ? `Komplett sats · ${setSize} ${noun.many}`
        : 'Per styck',
  };
};

/**
 * BR-9 — the label above the checkout quantity control.
 *
 * Stock and order quantity are ALWAYS counted in sales units, never in loose
 * items: a set listing holds N sets and sells sets, a per-item listing holds N
 * items and sells items. That one invariant is what keeps the Sharetribe stock
 * decrement correct without any conversion.
 */
export const quantityFieldLabel = publicData => {
  const { salesUnit } = resolveSalesUnit(publicData);
  const noun = getUnitNoun(publicData);
  return salesUnit === SALES_UNIT_SET ? 'Antal satser' : `Antal ${noun.many}`;
};

/** One option in the quantity picker, e.g. "2 satser" / "4 hjul". */
export const quantityOptionLabel = (publicData, count) => {
  const { salesUnit } = resolveSalesUnit(publicData);
  const noun = getUnitNoun(publicData);
  if (salesUnit === SALES_UNIT_SET) return `${count} ${count === 1 ? 'sats' : 'satser'}`;
  return `${count} ${count === 1 ? noun.one : noun.many}`;
};

/**
 * BR-9 — how many sales units the quantity picker should start on. A per-item
 * listing defaults to a full set (a buyer almost always wants four), capped by
 * what is actually in stock. A set listing always starts at one set.
 */
export const defaultOrderQuantity = (publicData, stock) => {
  const { salesUnit, setSize } = resolveSalesUnit(publicData);
  const available = Number.isFinite(Number(stock)) ? Number(stock) : null;
  if (salesUnit === SALES_UNIT_SET) return 1;
  const wanted = setSize;
  return available != null && available > 0 ? Math.min(wanted, available) : wanted;
};

/**
 * BR-4 — the stock row, shown for company listings only. `stock` is the
 * Sharetribe `currentStock` quantity; null/undefined means the listing does
 * not track stock at all (private sellers), so no row is shown.
 *
 * The count is NAMED, never bare: stock is counted in sales units, so a plain
 * "Få kvar (1)" means one wheel on a per-item listing and one four-wheel set
 * on a set listing — four times the goods and four times the money, from an
 * identical row. A bare quantity beside a price is the exact thing BR-3
 * forbids, so this says "Få kvar (1 fälg)" or "Få kvar (1 sats)".
 *
 * @param {number|null} stock      the Sharetribe currentStock quantity
 * @param {Object}      publicData the listing's publicData, for the unit noun
 * @returns {{label: string, value: string, level: string}|null}
 */
export const describeStock = (stock, publicData = {}) => {
  if (stock === null || stock === undefined) return null;
  const quantity = Number(stock);
  if (!Number.isFinite(quantity)) return null;

  if (quantity <= 0) return { label: 'Lager', value: 'Slutsåld', level: 'out' };

  const named = quantityOptionLabel(publicData, quantity);
  if (quantity <= LOW_STOCK_THRESHOLD)
    return { label: 'Lager', value: `Få kvar (${named})`, level: 'low' };
  return { label: 'Lager', value: `I lager (${named})`, level: 'in' };
};

/**
 * Switching the sales unit RE-INTERPRETS the stock that is already there.
 *
 * Stock counts sales units, and nothing converts it when the unit changes. A
 * listing holding 8 wheels at `piece` still says 8 after a switch to `set` —
 * but 8 now means 8 sets, i.e. 32 wheels. The seller would be offering four
 * times what they own, and buyers could order it.
 *
 * We deliberately do NOT convert the number automatically: dividing is lossy
 * (7 wheels is 1 set and 3 orphans) and only the seller knows what is on the
 * shelf. So we say plainly what the number now means and let them correct it.
 *
 * @param {Object} params
 * @param {number} params.stock      current Sharetribe stock, in the OLD unit
 * @param {string} params.from       the unit before the switch
 * @param {string} params.to         the unit being switched to
 * @param {Object} params.publicData listing publicData, for set size and noun
 * @returns {string|null} null when there is nothing to warn about
 */
export const stockMeaningWarning = ({ stock, from, to, publicData = {} }) => {
  const quantity = Number(stock);
  // Zero stock has no meaning to re-interpret, and an untracked listing has none.
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!from || !to || from === to) return null;

  const { setSize } = resolveSalesUnit({ ...publicData, salesUnit: to });
  // A "set" of one is the same thing as a piece, so nothing changes.
  if (setSize <= 1) return null;

  const noun = getUnitNoun(publicData);
  const sets = `${quantity} ${quantity === 1 ? 'sats' : 'satser'}`;
  const pieces = `${quantity * setSize} ${noun.many}`;

  return to === SALES_UNIT_SET
    ? `Lagersaldot ${quantity} betyder nu ${sets} — alltså ${pieces} totalt. ` +
        `Stämmer det? Ändra annars lagersaldot.`
    : `Lagersaldot ${quantity} betyder nu ${quantity} ${
        quantity === 1 ? noun.one : noun.many
      } i stället för ${sets} (${pieces}). Stämmer det? Ändra annars lagersaldot.`;
};

/**
 * BR-6 — soft plausibility check on a SET price. It warns, it never blocks.
 *
 * @param {Object} params
 * @param {number} params.unitAmount the entered price, in currency subunits
 * @param {Object} params.publicData
 * @param {Object} [params.floors]   override table, same shape as
 *   MIN_PLAUSIBLE_PIECE_PRICE_SEK, in major units
 * @returns {{implausible: boolean, perPiece: number, floor: number, message: string|null}}
 */
export const checkSetPricePlausibility = ({
  unitAmount,
  publicData,
  floors = MIN_PLAUSIBLE_PIECE_PRICE_SEK,
}) => {
  const { salesUnit, setSize } = resolveSalesUnit(publicData);
  const kind = getProductKind(publicData);
  const floor = floors[kind] || 0;
  const notApplicable = {
    implausible: false,
    perPiece: null,
    floor,
    message: null,
  };

  if (salesUnit !== SALES_UNIT_SET || setSize <= 1) return notApplicable;
  if (!Number.isFinite(unitAmount) || unitAmount <= 0) return notApplicable;

  // Subunits -> major units, so the floors above read as plain kronor.
  const perPiece = Math.round(unitAmount / setSize / 100);
  if (perPiece >= floor) return { ...notApplicable, perPiece };

  const noun = getUnitNoun(publicData);
  return {
    implausible: true,
    perPiece,
    floor,
    message:
      `Det här blir ${perPiece.toLocaleString('sv-SE')} kr per ${noun.one} ` +
      `för en sats om ${setSize}. Är priset verkligen för hela satsen, ` +
      `eller menade du per ${noun.one}?`,
  };
};

/**
 * Google Shopping / JSON-LD availability from the Sharetribe stock (BR-12).
 * A listing that does not track stock is treated as available — that is how it
 * behaves on the marketplace today.
 */
export const stockAvailability = stock => {
  if (stock === null || stock === undefined) return 'https://schema.org/InStock';
  return Number(stock) > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
};

/* ---------------------------------------------------------------------------
 * Freshness (BR-12)
 * ------------------------------------------------------------------------ */

/**
 * The Sharetribe listing API exposes `createdAt` but NOT an updated timestamp,
 * so "Uppdaterad {date}" cannot be read off the listing. We store our own
 * stamp in publicData and bump it from every place that changes price or
 * stock — the wizard, the CSV import and the supplier feed sync.
 *
 * Why NOT the existing `publicData.updateDate`: that field is a search-sort
 * score, not a fact. server/crons/handleUpdateListings.js rewrites it weekly
 * for every premium seller's listings with `now + random(0..60s)`, purely to
 * lift them in the "Senaste" sort. Showing it as "Uppdaterad" would tell a
 * buyer that a months-old, untouched ad was updated today — the opposite of
 * what BR-12 is for. Keep the two apart.
 */
export const PRICE_STOCK_UPDATED_AT_KEY = 'priceStockUpdatedAt';

/** ISO string to write into publicData when price or stock changes. */
export const priceStockUpdateStamp = (now = new Date()) => now.toISOString();

/** @returns {Date|null} */
export const getPriceStockUpdatedAt = (publicData = {}) => {
  const raw = publicData[PRICE_STOCK_UPDATED_AT_KEY];
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};
